<template>
  <footer
    class="study-footer py-3 position-md-sticky border_top_1 v3_color_teal_05"
  >
    <b-container class="v2_study_container grey-box py-4">
      <b-row>
        <b-col sm="12" xl="8" md="7">
          <b-row>
            <b-col
              md="3"
              cols="12"
              class="study-footer__navigation-text d-flex align-items-center"
            >
              <div class="d-flex align-items-center flex-wrap">
                <span class="mr-1">Navigation</span>
              </div>
            </b-col>
            <b-col
              md="9"
              cols="12"
              class="study-footer__progress-indicator d-flex align-items-center flex-wrap"
            >
              <template
                v-for="(question, i) in applicableQuestions"
              >
                <span
                  :id="`study-navigation-page-${question.id}`"
                  :class="[
                    'study-footer__progress-indicator-item',
                    'mx-1',
                    'my-1',
                    'clickable',
                    { current: question.id === currQuestionId },
                    { completed: isCompleted(question) },
                    { invalid: isInvalid(question) },
                  ]"
                  @click="$emit('go-to-question', questionData(question.id))"
                >
                  {{ i + 1 }}
                </span>
                <b-tooltip
                  placement="left"
                  :target="`study-navigation-page-${question.id}`"
                >
                  {{
                    question.id === currQuestionId
                    ? "This is the question you are currently answering."
                    : (
                      isCompleted(question)
                        ? "This question has been completed."
                        : "This question has not been completed yet and needs to be completed before completing the study."
                    )
                  }}
                </b-tooltip>
              </template>
            </b-col>
          </b-row>
        </b-col>
        <b-col
          sm="12"
          xl="4"
          md="5"
          class="study-footer__navigation-buttons d-flex flex-wrap justify-content-between mt-2 mt-md-0"
        >
          <b-button
            variant="white"
            :disabled="disablePrevious"
            @click="$emit('go-to-previous',checkQuestionCompleted)"
          >
            <i class="mdi mdi-arrow-left-bold mr-1" />
            Back
          </b-button>
          <b-button
            variant="primary"
            class="question-continue-button"
            :class="{ 'question-completed': (isCurrentQuestionCompleted && !allQuestionsAnswered) }"
            :disabled="isLastQuestion"
            v-on:click="$emit('go-to-next',checkQuestionCompleted)"
          >
            Continue
            <i class="mdi mdi-arrow-right-bold ml-1" />
          </b-button>
          <template>
            <div id="complete-study-wrapper" tabindex="0">
              <b-button
                :disabled="isSubmitDisabled"
                variant="primary"
                class="survey-progress-bar__btn-submit text-center"
                @click="$emit('complete-study')"
              >
                {{ isModuleQuestionnaire ? 'Complete Module' : 'Complete Study'}}
              </b-button>
            </div>
            <b-tooltip target="complete-study-wrapper" v-if="isSubmitDisabled">
              You cannot complete the study since one or more questions are not
              complete or have invalid responses. The status bar indicates
              question status (grey = not completed, green = complete, red =
              invalid response) and can be clicked to navigate to a question.
            </b-tooltip>
          </template>
        </b-col>
      </b-row>
      <!----- Interested Question Modal Start ----->
      <b-modal
        ref="interestedQuestionModal"
        size="xl"
        class="text-left job-offer-modal"
        centered
        footer-class="justify-content-center"
        :visible="(isCurrentQuestionCompleted && !allQuestionsAnswered && checkQuestionHaveInterestedQuestion && isShowInterestedQuestionModal)"
        hide-footer
        @hidden="onModalHidden"
      >
        <template slot="modal-header">
          <div class="w-100">
            <h5
              class="modal-title text-center font_size_1125rem font-weight-bold v3_color_teal mt-2"
            >
            <template v-if="interestedQuestionContent && interestedQuestionContent.header">
                <span v-html="interestedQuestionContent.header"/>
            </template>
            </h5>
          </div>
        </template>
        <div class="p-4 v3_color_teal">
          <template v-if="interestedQuestionContent && interestedQuestionContent.body">
              <div v-html="interestedQuestionContent.body"/>
          </template>
        </div>
        <div class="d-flex align-items-center justify-content-center">
          <b-button
            variant="v2_primary"
            class="p-4 text-center"
            size="sm"
            @click="onContinue"
            v-b-tooltip.hover
          >
            Close
          </b-button>
        </div>
    </b-modal>
    <!----- Interested Question Modal End ----->
    </b-container>
  </footer>
</template>

<script>
import Api from '@/providers/Api.js';
import {
  isApplicableQuestion,
  isCompleted,
  isCompletedMultiVariable,
  isInvalid,
} from '@/helpers/questionnaireHelper';
import { QUESTION_TYPES } from '@/store';

export default {
  name: 'StudyProgressBar',
  props: {
    questions: Array,
    variables: Array,
    survey: Object,
    currQuestionId: Number,
    isCurrQuestionInterested: Number,
    isSubmitDisabled: Boolean,
    isLastQuestion: Boolean,
    submitLoading: Boolean,
    isModuleQuestionnaire: '',
  },
  data: function () {
    return {
      checkIsInterestedQuestion: false,
      interestedQuestionContent: null,
      isShowInterestedQuestionModal: true
    }
  },
  created: function() {
    this.getInterestedQuestionContent();
  },
  computed: {
    applicableQuestions: function () {
      if (!this.survey || !Array.isArray(this.questions)) return [];
      return this.questions.filter((q) =>
        isApplicableQuestion(q, this.variables)
      );
    },
    disablePrevious: function () {
      return this.survey.current_question_index <= 0;
    },
    isQuestionnaireSupportJobPreference: function () {
      return (
        this.survey.questionnaire &&
        this.survey.questionnaire.is_support_job_preference
      );
    },
    currentQuestion: function () {
      return this.applicableQuestions.find((q) => q.id === this.currQuestionId);
    },
    isCurrentQuestionCompleted: function () {
      return this.isCompleted(this.currentQuestion);
    },
    allQuestionsAnswered: function () {
      let success = true;
      this.applicableQuestions.forEach((question) => {
        if (!this.isCompleted(question)) {
          success = false;
        }
      })
      return success;
    },
    checkQuestionCompleted: function () {
      if((this.isCurrentQuestionCompleted && !this.allQuestionsAnswered)){
        return true;
      }else{
        return false;
      }
    },
    checkQuestionHaveInterestedQuestion: function () {
      if(this.isCurrQuestionInterested === 1 ) return true;
      return false;
    }
  },
  methods: {
    isCompleted: function (question) {
      if (!question) return false;
      if (question.type === QUESTION_TYPES.MULTI_VARIABLE_SELECT_ONE) {
        return isCompletedMultiVariable(
          question,
          this.isQuestionnaireSupportJobPreference
        );
      }
      return isCompleted(question, this.isQuestionnaireSupportJobPreference);
    },
    isInvalid: function (question) {
      return isInvalid(question);
    },
    questionData: function(questionId) {
      const data = {
        qId : questionId,
        questionCompleted : this.checkQuestionCompleted
      };
      return data;
    },
    getInterestedQuestionContent: async function () {  
      try {
          this.interestedQuestionContent = await Api.getInterestedQuestionContent();
      } catch {
          this.$toasted.error(
              'Unable to update page content. Please try again later',
              {duration: 3000}
          );
      } finally {
      }
    },
    onModalHidden: function () {
      this.isShowInterestedQuestionModal = false;
      this.$refs.interestedQuestionModal.hide();
    },
    onContinue: function () {
      this.isShowInterestedQuestionModal = false;
      this.$refs.interestedQuestionModal.hide();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../../../sass/v3';

footer.study-footer {
  z-index: 99;
  bottom: 0;
  width: 100%;
  background-color: $phairify_v2_color_white;
  .container {
    padding-left: 30px;
    padding-right: 30px;
  }
  .study-footer__navigation-text {
    font: normal normal 600 24px/31px;
    letter-spacing: 0;
    color: $phairify_v2_color_med_gray;
    opacity: 1;
    & > div {
      max-height: 60px;
    }
  }
  .study-footer__progress-bar {
    font-weight: bold;
    color: $phairify_footer_grey_color;
  }
  .study-footer__navigation-buttons {
    justify-content: space-around;
    align-content: space-around;
    .btn {
      display: inline-flex;
      align-items: center;
      max-height: 30px;
      font: normal normal 550 16px/24px;
      letter-spacing: 0;
      color: $phairify_v2_color_primary_teal !important;
      background-color: #dee9ec;
      border: 1px solid $phairify_v2_color_primary_teal !important;
      opacity: 1;
      border-radius: 20px;
      &.question-continue-button {
        &.question-completed {
          background-color: #d0de3a !important;
        }
      }
    }

    i.mdi {
      font-size: 15px;
      padding-top: 2px;
      line-height: 1;
    }
  }
  #disabled-wrapper,
  #complete-study-wrapper {
    width: auto;
    display: inline-block;
    @media (min-width: 768px) and (max-width: 991px), (max-width: 445px) {
      width: 100%;
    }
    .survey-progress-bar__btn-submit {
      width: auto;
      height: 40px;
      background-color: #d0de3a !important;
      color: #000000 !important;
      border-radius: 0;
      border: none !important;
      @media (min-width: 768px) and (max-width: 991px), (max-width: 445px) {
        width: 100%;
        justify-content: center;
        margin-top: 0.5rem;
      }
      &:disabled {
        background-color: rgba(77,88,88,0.25) !important;
      }
    }
  }
  .study-footer__progress-indicator {
    .study-footer__progress-indicator-item {
      display: inline-block;
      width: 25px;
      height: 25px;
      background: #a3a9a9 0 0 no-repeat padding-box;
      opacity: 1;
      color: #ffffff;
      text-align: center;
      &.completed {
        background: #d0de3a 0 0 no-repeat padding-box;
      }
      &.current {
        background-color: $phairify_v2_color_primary_teal;
      }
      &.invalid {
        background-color: $phairify_color_red !important;
        border-color: $phairify_color_red !important;
      }
    }
  }
  .btn.btn-white:hover:not(.disabled) {
    background-color: #dee9ec !important;
  }
}
</style>
